import React, { useCallback, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { Form } from "@unform/web";
import * as Yup from "yup";
import getValidationErrors from "../../../utils/getValidationErrors";

import { useSelector } from "react-redux";

import { FormattedMessage, useIntl } from "react-intl";

import {
  Container,
  TitleContainer,
  Title,
  Session,
  FieldsContainer,
  ActionContainer,
  BadgeContainer,
} from "./styles";

import { useToast } from "../../../hooks/toast";
import { validateSingleFieldOnBlur } from "../../../utils/formValidations";
import api from "../../../service";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import InputWithHorizontalLabel from "../../../components/InputWithHorizontalLabel";
import SelectWithLabel from "../../../components/SelectWithLabel";
import Badge from "../../../components/Badge";
import Toggle from "../../../components/Toggle";
import credentialTypesConstants from "../../../constants/credentialTypes";

const NewAdditionalProduct = () => {
  const formRef = useRef(null);
  const intl = useIntl();
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [isCategory, setIsCategory] = useState(false);

  const history = useHistory();
  const credentialTypes = credentialTypesConstants();

  const schema = Yup.object().shape({
    title: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    english_title: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
  });

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        setLoading(true);
        if (loading) return;

        await schema.validate(data, { abortEarly: false });

        api
          .post(
            `/admin/ecommerce/chleba/category`,
            {
              title: data.title,
              englishTitle: data.english_title,
              credential: data.credential,
              credentialType: credentialTypes.find(
                  (item) => item.value === data.credential_type)?.value,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            setLoading(false);
            addToast({
              type: "success",
              title: intl.formatMessage({
                id: "success",
              }),
              description: intl.formatMessage({
                id: "success.category_creation",
              }),
            });
            history.push("/admin/categories");
          })
          .catch((err) => {
            setLoading(false);
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }
      }
    },
    [addToast, history, intl, loading, schema, token]
  );

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  return (
    <AnimatedMainContainer>
      <Container>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <TitleContainer>
            <Title>
              <FormattedMessage id="new_category" />
            </Title>
            <ActionContainer>
              <BadgeContainer onClick={() => history.push("/admin/categories")}>
                <Badge
                  color="#C9C9C9"
                  text={intl.formatMessage({ id: "goback" })}
                  fontSize="12px"
                />
              </BadgeContainer>
              <BadgeContainer onClick={() => formRef.current.submitForm()}>
                <Badge
                  color="#002244"
                  text={intl.formatMessage({ id: "save" })}
                  fontSize="12px"
                  loading={loading}
                />
              </BadgeContainer>
            </ActionContainer>
          </TitleContainer>
          <Session>
            <FieldsContainer>
              <InputWithHorizontalLabel
                name="title"
                placeholder={intl.formatMessage({
                  id: "title",
                })}
                label={intl.formatMessage({
                  id: "title",
                })}
                validationBlur={validationBlur}
                labelWidth="80px"
                required
              />
              <InputWithHorizontalLabel
                name="english_title"
                placeholder={intl.formatMessage({
                  id: "title_en",
                })}
                label={intl.formatMessage({
                  id: "title_en",
                })}
                validationBlur={validationBlur}
                labelWidth="80px"
                required
              />
              <Toggle
                name="credential"
                label={intl.formatMessage({ id: "is_credential" })}
                onClick={() => setIsCategory(!isCategory)}
              />
              <SelectWithLabel
                name="credential_type"
                label={intl.formatMessage({ id: "credential_type" })}
                placeholder={intl.formatMessage({ id: "credential_type" })}
                labelOrientation="horizontal"
                labelWidth="80px"
                options={credentialTypes}
                disabled={!isCategory}
              />
            </FieldsContainer>
          </Session>
        </Form>
      </Container>
    </AnimatedMainContainer>
  );
};

export default NewAdditionalProduct;
